/**
 * chordRespell.ts
 *
 * Finds iReal Pro-recognised spellings for chords whose Harmona spelling iReal Pro
 * cannot render natively.
 *
 * WHY THIS EXISTS
 * ---------------
 * iReal Pro only renders a fixed set of chord qualities with real chord
 * formatting (see IREAL_KNOWN_SUFFIXES in chordChartExport.ts). Anything else
 * falls back to *...* custom text, which displays but is not a real chord to
 * iReal Pro. Harmona can spell far more chords than that set covers, and the
 * long tail is effectively unbounded — there is no hope of enumerating every
 * rare extension a user might enter.
 *
 * So this module does NOT try to cover the long tail with a lookup table. It
 * uses two mechanisms, in order:
 *
 *   1. EQUIVALENTS — a deliberately tiny table of suffixes that mean the same
 *      chord but which iReal Pro spells differently (e.g. '-2' → '-add2').
 *      Same root, same chord, different spelling of the quality.
 *
 *   2. RESPELLING — walk the chord's own alternate spellings in READ order
 *      (easiest-to-read first, the ordering behind the Chord Symbol panel's
 *      READ setting) and take the first one iReal Pro renders natively. This is
 *      the general mechanism, and it scales to chords no table could cover
 *      because the alternates come from the analysis engine, not from a list.
 *
 * If neither finds anything, the chord keeps its Harmona spelling and the
 * exporter's existing *...* fallback handles it.
 *
 * READ vs FEEL ordering: the Chord Symbol panel sorts by legibility ("READ",
 * `cs_options_segments_notation`) or by root strength ("FEEL",
 * `cs_options_segments`). Respelling always uses READ order — the goal is a
 * symbol a player can read on a chart, not the perceptually strongest root.
 *
 * REUSE
 * -----
 * Nothing here touches the iReal Pro export pipeline or the chord chart state.
 * `resolveChart` takes candidate spellings from a caller-supplied function and
 * returns a plain description of what it would change. The iReal Pro export
 * applies the result to the exported chart only; a future in-app batch edit can
 * apply the same result to the real chordChart (with history/undo) without any
 * change to this module.
 */

import {
  ChordChartEntry,
  ChordSegment,
  irealRootNameForSegment,
  irealSuffixForSegment,
  isIrealNativeSuffix,
} from './chordChartExport';

// ---------------------------------------------------------------------------
// Equivalent spellings
// ---------------------------------------------------------------------------

/**
 * A rule renaming one of Harmona's suffixes to an iReal Pro-native suffix for
 * what is musically the same chord.
 *
 * Keep this table SHORT. It exists for chords iReal Pro can already render but
 * spells under a different name — not as a dumping ground for the long tail,
 * which is what the respelling path is for.
 *
 * The bar is that a player reading the chart plays the same chord. Anything
 * that changes the chord's quality or root does not belong here — that is what
 * the respelling path is for.
 */
export type EquivalentRule = {
  /** The (non-native) iReal Pro suffix Harmona's mapper produces. */
  from: string;
  /** The native iReal Pro suffix naming the same chord. */
  to: string;
  /**
   * Rewrites the segment so the exporter produces `to`.
   *
   * Kept in the same record as `from`/`to` on purpose: an earlier version had
   * the suffix table and the rewrite in two places, which meant adding an
   * equivalent silently did nothing unless you remembered to edit both.
   */
  rewrite: (segment: ChordSegment) => ChordSegment;
};

export const IREAL_EQUIVALENT_RULES: EquivalentRule[] = [
  {
    from: '-2',
    to: '-add2',
    // Harmona names this chord Cm2 → extension "2"; iReal Pro spells the same
    // chord '-add2', which comes from extension "add2".
    rewrite: (segment) => ({ ...segment, extension: 'add2' }),
  },
  {
    from: '-(4)',
    to: '-add4',
    // Harmona names this chord Cm(4) — get_cs.py puts added notes in parens —
    // which is the same chord as iReal Pro's '-add4'. Parenthesised suffixes can
    // never be native (parens mean "alternate chord" to iReal Pro), so without
    // this rule it would always fall back to *m(4)*.
    rewrite: (segment) => ({ ...segment, extension: 'add4' }),
  },
  {
    from: '(4)',
    to: 'add4',
    // Major counterpart of the rule above: C(4) is C add4.
    rewrite: (segment) => ({ ...segment, extension: 'add4' }),
  },

  // A 7th (or 9th) chord with an added 4th is an 11 chord — an 11 may or may
  // not include the 9th, just as a 13 may or may not, so the note content is
  // right either way. iReal Pro has no 'add4' spelling on top of a 7th, but
  // '11' is supported.
  //
  // This is a compromise on legibility, not on content: players routinely omit
  // the 11 even in a chord like C13, so 'add4' is how you specify that sound
  // deliberately, and many read C11 as C9sus instead. C11 is the less clear
  // symbol, but it is the one iReal Pro can render.
  {
    from: '7add4',
    to: '11',
    rewrite: (segment) => ({ ...segment, extension: '11' }),
  },
  {
    from: '9add4',
    to: '11',
    rewrite: (segment) => ({ ...segment, extension: '11' }),
  },
];

/** The equivalent rule for a suffix, if one exists and actually lands native. */
function findEquivalent(suffix: string): EquivalentRule | null {
  for (const rule of IREAL_EQUIVALENT_RULES) {
    if (rule.from === suffix && isIrealNativeSuffix(rule.to)) return rule;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Resolution types
// ---------------------------------------------------------------------------

/**
 * What we propose doing with one chord.
 *
 * Deliberately neutral about *why* a change is proposed, so the same type
 * carries iReal Pro respelling and the chart-wide respell commands.
 */
export type ChordResolution =
  /** Nothing to propose — already fine for whatever operation ran. */
  | { kind: 'unchanged' }
  /**
   * Replace the chord's segment.
   *
   * Both segments travel with the resolution so a review UI can render them as
   * real chord symbols (via assembleChordSymbol) rather than as raw iReal Pro
   * suffix text, which means nothing to a player reading the dialog.
   *
   * reason 'equivalent' — same chord, renamed to iReal Pro's spelling of it.
   * reason 'respelled'  — a different alternate spelling of the same voicing.
   * optionIndex is the position in the ordered candidate list ('respelled').
   */
  | {
    kind: 'changed';
    reason: 'equivalent' | 'respelled';
    fromSegment: ChordSegment;
    toSegment: ChordSegment;
    optionIndex?: number;
  }
  /**
   * Nothing native was found — keep Harmona's spelling, exporter wraps it in
   * *...* as custom text.
   *
   * Carries the segment so a review UI can list these chords alongside the
   * ones it can fix. Reporting only the fixable chords understates how many
   * chords iReal Pro cannot render, which is the more important number.
   */
  | { kind: 'fallback'; suffix: string; segment: ChordSegment };

/** One chart position and what we propose for it. */
export type ChartResolution = {
  /** Index into the chart array this resolution applies to. */
  index: number;
  resolution: ChordResolution;
};

/**
 * The outcome of a whole-chart operation.
 *
 * `lookupFailures` counts chords whose candidate lookup threw. Those chords are
 * still present in `resolutions` (as 'unchanged' or 'fallback'), so the
 * operation always completes; the count exists so the caller can tell the user
 * the result is incomplete rather than silently under-reporting suggestions.
 *
 * `lookupFailureReason` describes the FIRST such failure. Callers used to
 * report every failure as "analysis server unreachable", which is a guess: a
 * rejected lookup is just as likely to be a rate limit, a 500 on one voicing,
 * or a timeout — and the wrong guess sends whoever reads it looking in the
 * wrong place. Report what actually came back.
 */
export type ChartResolutionResult = {
  resolutions: ChartResolution[];
  lookupFailures: number;
  lookupFailureReason?: string;
};

/**
 * A short description of a failed lookup, for the review dialog's warning.
 *
 * Deliberately duck-typed rather than importing axios: this module has no
 * network dependency, and the shape it reads (`response.status`, `code`,
 * `message`) is common to every HTTP client.
 */
export function describeLookupError(err: any): string {
  const response = err && err.response;
  if (response && response.status) {
    if (response.status === 429) return 'analysis server rate limit reached';
    if (response.status >= 500) return `analysis server error (${response.status})`;
    // A 4xx means the request itself was wrong, and the server says how
    // (sentiserver.py returns {"error": ...}). That sentence is the whole
    // diagnosis — printing only the status number is what made a rejected
    // payload look like an unreachable server.
    const data = response.data;
    const serverMessage = data && typeof data.error === 'string' ? data.error : null;
    if (serverMessage) return `analysis server rejected the request: ${serverMessage}`;
    return `analysis server rejected the request (${response.status})`;
  }
  if (err && err.code === 'ECONNABORTED') return 'analysis server timed out';
  if (err && err.message) return err.message;
  return 'analysis server unreachable';
}

/** True when this resolution would actually change what the user sees. */
export function isChange(r: ChordResolution): boolean {
  return r.kind === 'changed';
}

/** The subset of resolutions that would actually change something. */
export function changesOnly(result: ChartResolutionResult): ChartResolution[] {
  return result.resolutions.filter(r => isChange(r.resolution));
}

// ---------------------------------------------------------------------------
// Single-chord resolution
// ---------------------------------------------------------------------------

/**
 * Renders a segment the way iReal Pro would display it, for showing the user
 * what a suggestion will look like (e.g. "Cm2" → "Cmadd2").
 */
export function irealSymbolForSegment(
  segment: ChordSegment,
  altSymbol: 'sharp-flat' | 'plusminus' | 'explicit' = 'explicit',
): string {
  const root = irealRootNameForSegment(segment);
  const suffix = irealSuffixForSegment(segment, altSymbol);
  const bass = segment.bass_pc !== segment.root_pc
    ? `/${irealRootNameForSegment({ ...segment, root_pc: segment.bass_pc })}`
    : '';
  return `${root}${suffix}${bass}`;
}

/**
 * Decides what to do with a single chord.
 *
 * `candidates` are the chord's alternate spellings in READ order (easiest to
 * read first) — i.e. `cs_options_segments_notation` from the analysis response.
 * Pass an empty array when they are unavailable; resolution then falls back to
 * the equivalents table alone, which is still useful and needs no network call.
 *
 * The chord's own spelling is checked first, so a chord that already works is
 * never "suggested" a change.
 */
export function resolveChord(
  segment: ChordSegment,
  candidates: ChordSegment[],
  altSymbol: 'sharp-flat' | 'plusminus' | 'explicit' = 'explicit',
): ChordResolution {
  const suffix = irealSuffixForSegment(segment, altSymbol);

  // 1. Already native — leave it alone.
  if (isIrealNativeSuffix(suffix)) {
    return { kind: 'unchanged' };
  }

  // 2. Equivalent spelling of the same chord.
  const equivalent = findEquivalent(suffix);
  if (equivalent) {
    return {
      kind: 'changed',
      reason: 'equivalent',
      fromSegment: segment,
      toSegment: equivalent.rewrite(segment),
    };
  }

  // 3. Walk alternate spellings in READ order; first native one wins.
  for (let i = 0; i < candidates.length; i++) {
    const candidate = candidates[i];
    // Skip the chord's own spelling — step 1 already rejected it.
    if (isSameSpelling(candidate, segment)) continue;

    const candidateSuffix = irealSuffixForSegment(candidate, altSymbol);

    // A candidate may be native outright, or native only after an equivalent
    // rename. In the latter case the rewritten segment is what gets proposed —
    // proposing the raw candidate would suggest a spelling that still is not
    // native, and the export would wrap it in *...* exactly as before.
    const candidateEquivalent = findEquivalent(candidateSuffix);
    const toSegment = isIrealNativeSuffix(candidateSuffix)
      ? candidate
      : (candidateEquivalent ? candidateEquivalent.rewrite(candidate) : null);

    if (!toSegment) continue;

    // Guarantee: never propose a chord iReal Pro cannot render. Re-checking the
    // final segment (rather than trusting the branch above) also catches an
    // equivalent rule whose `rewrite` does not actually produce its `to`.
    if (!isIrealNativeSuffix(irealSuffixForSegment(toSegment, altSymbol))) continue;

    return {
      kind: 'changed',
      reason: 'respelled',
      fromSegment: segment,
      toSegment,
      optionIndex: i,
    };
  }

  // 4. Nothing native anywhere — exporter wraps it in *...*.
  return { kind: 'fallback', suffix, segment };
}

// ---------------------------------------------------------------------------
// Whole-chart resolution
// ---------------------------------------------------------------------------

/**
 * A chord's alternate spellings in both of the Chord Symbol panel's orderings.
 *
 * read: `cs_options_segments_notation` — easiest to READ (legibility). The
 *       top entry can have a different root than the chord's own.
 * feel: `cs_options_segments` — sorted by how the chord FEELS (root strength).
 *
 * Both come back from a single analysis response, so a provider that fetches
 * one has already paid for the other. Carrying both means a respell followed
 * by an ordering respell costs no second round-trip.
 */
export type ChordCandidates = {
  read: ChordSegment[];
  feel: ChordSegment[];
};

/** Which ordering an operation should pick from. */
export type CandidateOrdering = 'read' | 'feel';

/**
 * Supplies the alternate spellings for one chord.
 *
 * Chart entries store only the chosen segment plus the voicing, so alternates
 * have to be re-derived from `voicing`. Implementations should cache by
 * voicing: charts repeat voicings heavily, and identical voicings always
 * resolve identically.
 *
 * Returning empty arrays is always safe — that chord simply goes unchanged.
 */
export type CandidateProvider = (
  entry: ChordChartEntry,
  index: number,
) => Promise<ChordCandidates>;

/** Empty candidates, for providers that have nothing to offer. */
export const NO_CANDIDATES: ChordCandidates = { read: [], feel: [] };

/**
 * Resolves every chord in a chart.
 *
 * Returns one entry per *chord* (separators, blanks and segment-less entries
 * are skipped), including the ones needing no change — callers that only want
 * the changes can filter with `isChange`. Reporting the no-change chords too
 * lets a review UI show "14 chords, 3 suggestions" without a second pass.
 *
 * Order is preserved, and `index` refers to the original chart array, so the
 * result can be applied to either the exported copy or the live chart.
 */
export async function resolveChart(
  chart: ChordChartEntry[],
  getCandidates: CandidateProvider,
  altSymbol: 'sharp-flat' | 'plusminus' | 'explicit' = 'explicit',
): Promise<ChartResolutionResult> {
  const results: ChartResolution[] = [];
  let failures = 0;
  let failureReason: string | undefined = undefined;

  for (let i = 0; i < chart.length; i++) {
    const entry = chart[i];
    if (entry.isSeparator || !entry.segment) continue;

    // Cheap check first: a chord that already renders natively needs no
    // candidates at all, so it costs no network round-trip.
    const suffix = irealSuffixForSegment(entry.segment, altSymbol);
    if (isIrealNativeSuffix(suffix)) {
      results.push({ index: i, resolution: { kind: 'unchanged' } });
      continue;
    }

    // Equivalents also need no candidates.
    const equivalent = findEquivalent(suffix);
    if (equivalent) {
      results.push({
        index: i,
        resolution: {
          kind: 'changed',
          reason: 'equivalent',
          fromSegment: entry.segment,
          toSegment: equivalent.rewrite(entry.segment),
        },
      });
      continue;
    }

    // Only now is a candidate lookup worth its cost.
    let candidates: ChordCandidates = NO_CANDIDATES;
    try {
      candidates = await getCandidates(entry, i);
    } catch (err) {
      // Let the caller decide whether a failed lookup is worth surfacing;
      // resolution itself degrades to the *...* fallback either way.
      console.warn('[chordRespell] candidate lookup failed for index', i, err);
      failures++;
      if (!failureReason) failureReason = describeLookupError(err);
    }

    results.push({
      index: i,
      resolution: resolveChord(entry.segment, candidates.read, altSymbol),
    });
  }

  return { resolutions: results, lookupFailures: failures, lookupFailureReason: failureReason };
}

// ---------------------------------------------------------------------------
// Chart-wide respelling by ordering
// ---------------------------------------------------------------------------

/**
 * Respells every chord in the chart to the top-ranked spelling in one ordering
 * — the batch form of the Chord Symbol panel's READ / FEEL setting. This can
 * change a chord's root, since the top-ranked spelling need not share it.
 *
 * 'read' → "Respell chords by legibility"
 * 'feel' → "Respell chords by feeling"
 *
 * Unlike `resolveChart` this has no notion of iReal Pro at all: it simply takes
 * candidate[0] in the chosen ordering. Chords already spelled that way come
 * back 'unchanged', so a review UI can show only the real changes, and the
 * result feeds the same `applyResolutions`.
 *
 * Every chord needs a lookup here (there is no cheap pre-check as there is for
 * respelling), so a caching provider matters much more for this operation.
 */
export async function respellChartByOrdering(
  chart: ChordChartEntry[],
  getCandidates: CandidateProvider,
  ordering: CandidateOrdering,
  altSymbol: 'sharp-flat' | 'plusminus' | 'explicit' = 'explicit',
): Promise<ChartResolutionResult> {
  const results: ChartResolution[] = [];
  let failures = 0;
  let failureReason: string | undefined = undefined;

  for (let i = 0; i < chart.length; i++) {
    const entry = chart[i];
    if (entry.isSeparator || !entry.segment) continue;

    let candidates: ChordCandidates = NO_CANDIDATES;
    try {
      candidates = await getCandidates(entry, i);
    } catch (err) {
      console.warn('[chordRespell] candidate lookup failed for index', i, err);
      failures++;
      if (!failureReason) failureReason = describeLookupError(err);
    }

    const ranked = ordering === 'read' ? candidates.read : candidates.feel;
    const top = ranked.length > 0 ? ranked[0] : null;

    if (!top || isSameSpelling(top, entry.segment)) {
      results.push({ index: i, resolution: { kind: 'unchanged' } });
      continue;
    }

    results.push({
      index: i,
      resolution: {
        kind: 'changed',
        reason: 'respelled',
        fromSegment: entry.segment,
        toSegment: top,
        optionIndex: 0,
      },
    });
  }

  return { resolutions: results, lookupFailures: failures, lookupFailureReason: failureReason };
}

/** True when two segments are the same spelling of the same chord. */
function isSameSpelling(a: ChordSegment, b: ChordSegment): boolean {
  return a.root_pc === b.root_pc &&
    a.third_quality === b.third_quality &&
    a.extension === b.extension &&
    a.bass_pc === b.bass_pc;
}

// ---------------------------------------------------------------------------
// Applying resolutions
// ---------------------------------------------------------------------------

/**
 * Returns a copy of the chart with the given resolutions applied.
 *
 * `accepted` selects which resolutions to apply — pass all of them for
 * "Accept All", an empty array for "Keep All", or a subset for per-chord
 * choices. Resolutions that change nothing are ignored.
 *
 * Only `segment` is rewritten. `name` (the Harmona display symbol) is left
 * alone here because building it needs App-level context (assembleChordSymbol
 * and the locked key). The iReal Pro exporter reads `segment`, not `name`, so
 * export is correct either way; an in-app batch edit should rebuild `name`
 * from the new segment after calling this.
 */
export function applyResolutions<T extends ChordChartEntry>(
  chart: T[],
  accepted: ChartResolution[],
): T[] {
  if (accepted.length === 0) return chart;

  const byIndex = new Map<number, ChordResolution>();
  for (const item of accepted) {
    if (isChange(item.resolution)) byIndex.set(item.index, item.resolution);
  }
  if (byIndex.size === 0) return chart;

  return chart.map((entry, i) => {
    const resolution = byIndex.get(i);
    if (!resolution || !entry.segment) return entry;
    if (resolution.kind !== 'changed') return entry;
    // Both reasons carry the finished segment, so there is one case to apply.
    return { ...entry, segment: resolution.toSegment };
  });
}
